import React, { useCallback, useEffect } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import {
  UiTooltipText,
  TOOLTIPS_TEXTS,
} from '@mais-retorno-front/components/ui-tooltip-text';

import {
  MR_SCHEMA,
  OPEN_GRAPH_IMAGE,
} from '@mais-retorno-front/constants/mais-retorno-description';
import { IUiTitleProps } from '@mais-retorno-front/libs/ui-title';
import { useTheme } from '@mui/material/styles';
import { useAlert } from '@mais-retorno-front/hooks/alert';
import { FUND_SEO } from '@mais-retorno-front/constants/seo';
import { Skeleton } from '@mais-retorno-front/components/skeleton';
import { WebPageSchema } from '@mais-retorno-front/components/seo';
import {
  CHART_OPTIONS,
  formatCnpj,
  formatNumber,
} from '@mais-retorno-front/libs/util-helpers';
import { DEFAULT_ROUTES } from '@mais-retorno-front/constants/routes';

import { IConsistencyTableProps } from '@mais-retorno-front/libs/ui-consistency-table';
import { IUiFundWalletMacroProps } from '@mais-retorno-front/libs/ui-fund-wallet-macro';
import { ISheetPageProps } from '@mais-retorno-front/models/sheet-page.model';
import { IFeatureProfitChartProps } from '@mais-retorno-front/libs/feature-profit-chart';
import { ICardChartProps } from '@mais-retorno-front/components/ui-card-chart';
import { addStructuredData } from '@mais-retorno-front/services/structured-data';
import { IProfitabilityRatiosProps } from '@mais-retorno-front/libs/ui-profitability-ratios';
import { useBookmarkAsset } from '@mais-retorno-front/core-business/main/bookmark-asset';
import { REVALIDATE_TIME_PAGES } from '@mais-retorno-front/constants/revalidate-time-pages';

import { makeSheetFundHeader } from '@mais-retorno-front/data-access-test-2/factories';

import { useSheetFundHeader } from '@mais-retorno-front/hooks/sheet-funds/sheet-fund-header';
import { useSheetFundStats } from '@mais-retorno-front/hooks/sheet-funds/sheet-fund-stats';
import { useSheetFundSeries } from '@mais-retorno-front/hooks/sheet-funds/sheet-fund-series';
import { useSheetFundWallet } from '@mais-retorno-front/hooks/sheet-funds/sheet-fund-wallet';
import { useSheetFundPolicies } from '@mais-retorno-front/hooks/sheet-funds/sheet-fund-policies';
import { useSheetFundRelated } from '@mais-retorno-front/hooks/sheet-funds/sheet-fund-related';
import { useSheetFundsBenchmarks } from '@mais-retorno-front/hooks/sheet-funds/sheet-funds-benchmarks';
import SectionTabsContext from '@mais-retorno-front/context/section-tabs';
import type { ButtonProps } from '@core/design-system/button';
import {
  SHEET_TABS_ID_SECTIONS,
  SHEET_TABS_SECTIONS,
} from '@mais-retorno-front/libs/ui-scrollspy-tabs/constant';
import MovableWindows from '@mais-retorno-front/components/movable-windows';
import { useSponsoredSheets } from '@mais-retorno-front/components/retorno-ads';
import { HistoricalProfitabilityTable } from '@core/design-system/table/historical-profitability';
import { useSnackbar } from '@core/design-system/snackbar';
import { usePeriod } from '@core/design-system/period-select';
import { useAuth } from '@core/features-core/auth/client';
import UiFundRelated from '@mais-retorno-front/modules/sheet-fund/related-funds';
import { Star, Download } from 'lucide-react';

const RetornoAds = dynamic(
  () =>
    import('@mais-retorno-front/components/retorno-ads').then(
      (module) => module.RetornoAds.SpacingRetornoAds
    ),
  { ssr: false }
);

const Box = dynamic(() => import('@mui/material/Box'));

const Button = dynamic<ButtonProps>(() =>
  import('@core/design-system/button').then((mod) => mod.Button)
);

const Grid = dynamic(() => import('@mui/material/Grid'));

const ComparisonButton = dynamic(() =>
  import('@mais-retorno-front/modules/sheet-fund/comparison-button').then(
    (m) => m.ComparisonButton
  )
);
const CtaLinkButton = dynamic(() =>
  import('@mais-retorno-front/styles').then((m) => m.CtaLinkButton)
);

const AiOutlineLineChart = dynamic<any>(() =>
  import('@react-icons/all-files/ai/AiOutlineLineChart').then(
    (mod) => mod.AiOutlineLineChart
  )
);

const UiSheetFundHeader = dynamic(
  () =>
    import('@mais-retorno-front/libs/ui-sheet-fund-header').then(
      (mod) => mod.UiSheetFundHeader
    ),
  {
    loading: () => <Skeleton width="100%" height="500px" />,
  }
);

const UiFundChips = dynamic(
  () =>
    import('@mais-retorno-front/libs/ui-fund-chips').then(
      (mod) => mod.UiFundChips
    ),
  {
    loading: () => <Skeleton width="100%" height="500px" />,
  }
);

const UiTitle = dynamic<IUiTitleProps>(() =>
  import('@mais-retorno-front/libs/ui-title').then((mod) => mod.UiTitle)
);

const UiPreOperationalContent = dynamic<any>(
  () =>
    import('@mais-retorno-front/libs/ui-pre-operational-content').then(
      (mod) => mod.UiPreOperationalContent
    ),
  { ssr: false }
);

const UiProfitabilityRatios = dynamic<IProfitabilityRatiosProps>(
  () =>
    import('@mais-retorno-front/libs/ui-profitability-ratios').then(
      (mod) => mod.UiProfitabilityRatios
    ),
  { ssr: false }
);

const UiConsistencyTable = dynamic<IConsistencyTableProps>(
  () =>
    import('@mais-retorno-front/libs/ui-consistency-table').then(
      (mod) => mod.UiConsistencyTable
    ),
  { ssr: false }
);

const SocialSharePopover = dynamic<any>(
  () =>
    import('@mais-retorno-front/components/social-share-popover').then(
      (mod) => mod.SocialSharePopover
    ),
  {
    loading: () => <Skeleton width="30px" height="30px" />,
  }
);

const DefaultLayoutTools = dynamic(
  () => import('@mais-retorno-front/layouts/default-tools')
);

const FeatureProfitChart = dynamic<IFeatureProfitChartProps>(
  () =>
    import('@mais-retorno-front/libs/feature-profit-chart').then(
      (module) => module.FeatureProfitChart
    ),
  {
    loading: () => <Skeleton width="100%" height="300px" />,
  }
);

const UiCardChart = dynamic<ICardChartProps>(
  () =>
    import('@mais-retorno-front/components/ui-card-chart').then(
      (module) => module.UiCardChart
    ),
  { ssr: false }
);

const UiFundWalletMacro = dynamic<IUiFundWalletMacroProps>(
  () =>
    import('@mais-retorno-front/libs/ui-fund-wallet-macro').then(
      (module) => module.UiFundWalletMacro
    ),
  { ssr: false }
);

const UiScrollspyTabs = dynamic<any>(
  () =>
    import('@mais-retorno-front/libs/ui-scrollspy-tabs').then(
      (module) => module.UiScrollspyTabs
    ),
  {
    loading: () => <Skeleton width="100%" height="40px" />,
  }
);

const SheetFundsPolices = dynamic(
  () =>
    import('@mais-retorno-front/components/sheet-funds-polices').then(
      (module) => module.SheetFundsPolices
    ),
  {
    loading: () => <Skeleton width="100%" height="40px" />,
  }
);

const SponsoredFundHeader = dynamic(() =>
  import('@mais-retorno-front/components/retorno-ads').then(
    (module) => module.RetornoAds.SponsoredFundHeader
  )
);

const SponsoredHoc = dynamic(() =>
  import('@mais-retorno-front/components/sponsored-hoc').then(
    (module) => module.SponsoredHoc
  )
);

const MutualFundsDisclaimer = dynamic(() =>
  import('@mais-retorno-front/modules/fii/disclaimer').then(
    (module) => module.MutualFundsDisclaimer
  )
);

const ManagerDataAlert = dynamic(() =>
  import('@mais-retorno-front/modules/sheet-fund/manager-data-alert').then(
    (module) => module.ManagerDataAlert
  )
);

const FundPage = ({ fundHeader: staticHeader }: ISheetPageProps) => {
  const router = useRouter();
  const theme = useTheme();

  const {
    userIsLogged: authenticated,
    accessToken: token,
    userIsPremium,
    initialized,
  } = useAuth();

  const { tabSelected }: any = SectionTabsContext();

  const { fundHeader, updateHeader } = useSheetFundHeader({
    initialHeader: staticHeader,
  });
  const { calculatedPeriod, calcPeriod } = usePeriod();

  const { fundStats, getStats, loadingStats } = useSheetFundStats();
  const { fundWallet, getWallet } = useSheetFundWallet();
  const { fundPolicy, getFundPolicy, loadingPolicies } = useSheetFundPolicies();
  const { getRelatedFunds, relatedFunds } = useSheetFundRelated();
  const {
    getSeries,
    loadingSeries,
    drawDownSeries,
    netWorthSeries,
    profitSeries,
    quotaHolderSeries,
    volatilitySeries,
  } = useSheetFundSeries();

  const { benchmarkQuotes, selectedBenchmarks, onChangeSelectedBenchmarks } =
    useSheetFundsBenchmarks({
      initialBenchmark: fundHeader.index,
      fundSlug: fundHeader.unique_slug,
    });

  useEffect(() => {
    getWallet(staticHeader.unique_slug);
    getFundPolicy(staticHeader.unique_slug);
    getRelatedFunds(staticHeader.unique_slug);

    updateHeader(staticHeader.unique_slug).then((fundHeader) => {
      if (!fundHeader || fundHeader.status === 'pre-operacional') return;

      getStats({ fundSlug: staticHeader.unique_slug });

      getSeries({
        fundSlug: staticHeader.unique_slug,
        start_date: fundHeader.first_quote_date,
        end_date: fundHeader.last_quote_date,
      });
    });
  }, [staticHeader]);

  useEffect(() => {
    calcPeriod('otimo', [
      {
        startDate: fundHeader.first_quote_date,
        endDate: fundHeader.last_quote_date,
      },
    ]);
  }, [fundHeader]);

  useEffect(() => {
    if (!fundHeader) return;

    const fundSchema = {
      '@context': 'https://schema.org',
      '@type': 'InvestmentFund',
      name: fundHeader.name.toUpperCase(),
      alternateName: fundHeader.nicename.toUpperCase(),
      category: `${fundHeader.class.name ? fundHeader.class.name : ''} - ${
        fundHeader?.subclass?.name ? fundHeader.subclass.name : ''
      }`,
      identifier: {
        '@type': 'PropertyValue',
        name: 'CNPJ',
        value: formatCnpj(fundHeader.cnpj),
      },
      amount: {
        '@type': 'MonetaryAmount',
        name: 'Patrimônio líquido',
        currency: 'BRL',
        value: fundHeader.networth,
      },
      description: `${fundHeader.name} - ${formatCnpj(fundHeader.cnpj)} ${
        fundHeader.asset_managers[0]?.nicename
          ? ', da gestora ' + fundHeader.asset_managers[0].nicename
          : ''
      }`,
    };

    if (fundHeader?.admin?.nicename) {
      fundSchema['broker'] = {
        '@type': 'Organization',
        legalName: fundHeader.admin.nicename,
        taxID: formatCnpj(fundHeader.admin.cnpj),
        description: 'Administrador',
      };
    }

    if (fundHeader.asset_managers.length > 0) {
      fundSchema['provider'] = {
        '@type': 'Organization',
        legalName: fundHeader.asset_managers[0].nicename,
        taxID: formatCnpj(fundHeader.asset_managers[0].cnpj),
        description: 'Gestor',
      };
    }

    addStructuredData(fundSchema);
  }, [fundHeader]);

  useEffect(() => {
    if (!fundHeader || !authenticated) return;

    onVerifyBookmarkAsset(fundHeader.id);
  }, [fundHeader, authenticated]);

  const onChangePeriodType = useCallback(
    (period) => {
      calcPeriod(period, [
        {
          startDate: fundHeader.first_quote_date,
          endDate: fundHeader.last_quote_date,
        },
      ]);
    },
    [calcPeriod, fundHeader]
  );

  const breadcrumbs = [
    {
      label: 'Melhores Fundos Investimentos',
      url: '/melhores-fundos',
    },
    { label: fundHeader.nicename.toUpperCase(), url: '' },
  ];

  const { dispatchLoginAlert } = useAlert();
  const { openSnackbar } = useSnackbar();
  const { fundAlreadyFavorite, onCreateBookmarkAsset, onVerifyBookmarkAsset } =
    useBookmarkAsset();

  const favoriteFund = async () => {
    if (!initialized) return;

    if (!authenticated) {
      return dispatchLoginAlert(
        'Para favoritar o fundo você precisa estar logado'
      );
    }

    if (fundAlreadyFavorite) {
      openSnackbar({
        type: 'info',
        message: 'Ativo já está na lista de Favoritos',
      });

      return;
    }

    const asset = {
      asset_type: 'fund',
      identifier: String(fundHeader.id),
    };

    const { error, message } = await onCreateBookmarkAsset(token, asset);

    if (!error) {
      openSnackbar({
        type: 'success',
        message,
      });
    } else {
      openSnackbar({
        type: 'error',
        message,
      });
    }
  };

  const handlerDownloadPDF = async () => {
    if (!initialized) return;

    if (!authenticated) {
      return dispatchLoginAlert('Para baixar o PDF você precisa estar logado');
    }

    window.print();
  };

  const handleCreateSimulation = () => {
    router.push({
      pathname: DEFAULT_ROUTES.createIncomeSimulation,
      query: { asset_identifier: `${fundHeader.cnpj}:fi` },
    });
  };

  const {
    managerDetail,
    investmentList,
    sponsoredVideo,
    isSponsored,
    fundDescriptionDocument,
    description: fundDescription,
    monthlyReport,
    youtubeId,
    ctaContact,
  } = useSponsoredSheets({
    cnpj: fundHeader?.cnpj.toString(),
    slug: fundHeader.asset_managers?.[0]?.slug,
  });

  return (
    <DefaultLayoutTools showFooterServiceTerms breadcrumbs={breadcrumbs}>
      <NextSeo
        title={FUND_SEO.pageTitle(fundHeader?.nicename, fundHeader?.cnpj)}
        description={FUND_SEO.pageDescription(
          fundHeader?.nicename,
          fundHeader?.cnpj
        )}
        canonical={FUND_SEO.baseUrl(fundHeader?.unique_slug)}
        openGraph={{
          type: 'website',
          url: FUND_SEO.baseUrl(fundHeader?.unique_slug),
          title: FUND_SEO.pageTitle(fundHeader?.nicename, fundHeader?.cnpj),
          description: FUND_SEO.pageDescription(
            fundHeader?.nicename,
            fundHeader?.cnpj
          ),
          site_name: MR_SCHEMA.organizationName,
          locale: MR_SCHEMA.organizationLocale,
          images: [OPEN_GRAPH_IMAGE],
        }}
      />

      <WebPageSchema
        url={FUND_SEO.baseUrl(fundHeader?.unique_slug)}
        name={FUND_SEO.pageTitle(fundHeader?.nicename, fundHeader?.cnpj)}
        description={FUND_SEO.pageDescription(
          fundHeader?.nicename,
          fundHeader?.cnpj
        )}
      />

      <Box mb={6}>
        <RetornoAds isSponsored={isSponsored} />

        <SponsoredHoc isSponsored={isSponsored}>
          <SponsoredFundHeader
            nicename={fundHeader.nicename}
            favoriteFund={favoriteFund}
            handlerDownloadPDF={handlerDownloadPDF}
            fundHeader={fundHeader}
            managerSite={managerDetail?.site}
            handleCreateSimulation={handleCreateSimulation}
            fundStats={fundStats}
            loadingStats={loadingStats}
            ctaContact={ctaContact}
          />
        </SponsoredHoc>

        <SponsoredHoc isSponsored={!isSponsored}>
          <Grid
            spacing={2}
            container
            style={{ marginBottom: 10 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item xs={12} lg={6} xl={10}>
              <UiTitle title={fundHeader.nicename} type="h1" />
            </Grid>

            <Grid item xs={12} md={4} lg={12} xl={2}>
              <Box
                display="flex"
                gap={1}
                displayPrint="none"
                sx={{
                  '@media (min-width: 1536px)': {
                    justifyContent: 'flex-end',
                  },
                }}
              >
                <Button
                  sx={{ minWidth: 0, padding: 1 }}
                  variant="contained"
                  color="secondary"
                  onClick={favoriteFund}
                  aria-label="Favoritar fundo"
                >
                  <Star />
                </Button>
                <Button
                  sx={{ minWidth: 0, padding: 1 }}
                  variant="contained"
                  color="secondary"
                  onClick={handlerDownloadPDF}
                  aria-label="Baixar PDF"
                >
                  <Download />
                </Button>
                <SocialSharePopover
                  tooltipText={`Compartilhar rentabilidade do fundo ${fundHeader.nicename}`}
                  description="Dá uma olhada nesse fundo"
                  url={`https://maisretorno.com${router.asPath}`}
                  style={{
                    borderRadius: '6px',
                    backgroundColor: theme.palette.secondary.main,
                    margin: 0,
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <UiFundChips fundHeader={fundHeader} />
            </Grid>
          </Grid>

          <Grid
            spacing={2}
            container
            style={{ marginBottom: 10 }}
            alignItems="center"
          >
            <Grid displayPrint={'none'} item xs={12} md={4} lg={3} xl={2}>
              <ComparisonButton
                fundCnpj={fundHeader.cnpj}
                benchmark={fundHeader.index}
                buttonProps={{ fullWidth: true }}
              />
            </Grid>

            <Grid item xs={12} md={4} lg={3} xl={2}>
              <CtaLinkButton
                fullWidth
                variant="outlined"
                endIcon={<AiOutlineLineChart size={20} />}
                onClick={handleCreateSimulation}
                id="sheet-fund-create-simulation"
                sx={{ textTransform: 'none' }}
              >
                Criar Simulação
              </CtaLinkButton>
            </Grid>
          </Grid>
        </SponsoredHoc>

        <ManagerDataAlert hasComplementaryData={fundHeader?.has_comp_data} />

        <UiSheetFundHeader
          fundHeader={isSponsored ? null : fundHeader}
          fundStats={fundStats}
          loadingStats={loadingStats}
          useLink
          routesLink={DEFAULT_ROUTES}
          Link={Link}
        />

        <MutualFundsDisclaimer
          fundSlug={fundHeader?.cnpj?.toString()}
          fiType={'Fundo'}
        />

        <UiScrollspyTabs sections={SHEET_TABS_SECTIONS} top={58} offset={30} />

        {fundHeader.status === 'pre-operacional' ? (
          <UiPreOperationalContent />
        ) : tabSelected === SHEET_TABS_ID_SECTIONS.MOVABLE_WINDOWS ? (
          <MovableWindows
            withSuccessRate
            nicename={fundHeader.nicename}
            assetsIdentifiers={`${fundHeader.cnpj}:fi`}
            id={SHEET_TABS_ID_SECTIONS.MOVABLE_WINDOWS}
          />
        ) : (
          <>
            <Box
              id={SHEET_TABS_ID_SECTIONS.PROFIT}
              sx={{
                mt: 2,
                pb: 5,
              }}
            >
              <Box style={{ breakInside: 'avoid' }}>
                <FeatureProfitChart
                  cardSubTitle={fundHeader.nicename}
                  firstQuote={fundHeader.first_quote_date}
                  lastQuote={fundHeader.last_quote_date}
                  lastUpdate={fundHeader.last_update}
                  period={calculatedPeriod}
                  series={profitSeries}
                  selectedBenchmarks={selectedBenchmarks}
                  setSelectedBenchmarks={onChangeSelectedBenchmarks}
                  benchmarkQuotes={benchmarkQuotes}
                  onChangePeriodType={onChangePeriodType}
                  loading={loadingSeries}
                  emptyText="Buscando cotações..."
                  hasWatermark
                  UiTooltip={
                    <UiTooltipText text={TOOLTIPS_TEXTS.profitabilityFund} />
                  }
                />
              </Box>
              <Box style={{ breakInside: 'avoid' }}>
                <SheetFundsPolices
                  fundManager={managerDetail}
                  investmentList={investmentList}
                  monthlyReport={monthlyReport}
                  isSponsored={isSponsored}
                  fundPolices={fundPolicy}
                  sponsoredVideo={sponsoredVideo}
                  managerSlug={fundHeader?.asset_managers?.[0]?.slug}
                  fundDescriptionDocument={fundDescriptionDocument}
                  fundDescription={fundDescription}
                  sponsoredYoutubeVideo={youtubeId}
                  hasComplementaryData={fundHeader?.has_comp_data}
                />
              </Box>

              <Box my={4}>
                <HistoricalProfitabilityTable
                  assetName={fundHeader.nicename}
                  formatDecimal={false}
                  isLoadingStats={false}
                  stats={fundStats}
                  defaultBenchmark={fundHeader.index}
                  tableProps={{
                    titleVariant: 'h4',
                    backgroundColor: theme.palette.primary.main,
                    textColor: theme.palette.primary.contrastText,
                  }}
                  userIsPrime={userIsPremium}
                />
              </Box>

              <Box mt={2} style={{ breakInside: 'avoid' }}>
                <UiProfitabilityRatios
                  fundName={fundHeader.nicename}
                  statsTimeFrame={fundStats?.stats?.timeframe}
                />
              </Box>
              <Box mt={2} style={{ breakInside: 'avoid' }}>
                <UiConsistencyTable
                  fundName={fundHeader.nicename}
                  fundStats={fundStats?.stats}
                  UiTooltip={
                    <UiTooltipText
                      text={TOOLTIPS_TEXTS.statisticsProfitComparison}
                    />
                  }
                />
              </Box>
            </Box>

            <Box
              component="section"
              style={{ paddingTop: theme.spacing(1), breakInside: 'avoid' }}
              id={SHEET_TABS_ID_SECTIONS.WALLET}
            >
              <UiFundWalletMacro
                cardTitle="Composição da carteira"
                cardSubTitle={fundHeader.nicename}
                walletMacro={fundWallet}
                assetSlug={fundHeader.unique_slug}
                assetsAreLink={false}
                Link={Link}
              />
            </Box>

            <Box
              style={{ paddingTop: theme.spacing(5) }}
              id={SHEET_TABS_ID_SECTIONS.CHARTS}
            >
              <Grid container spacing={4} style={{ breakInside: 'avoid' }}>
                <Grid item xs={12} sm={6}>
                  <UiCardChart
                    hasWatermark
                    cardTitle="Cotistas"
                    loading={loadingSeries}
                    cardSubTitle={fundHeader.nicename}
                    tooltip={TOOLTIPS_TEXTS.quotaHolders}
                    series={quotaHolderSeries}
                    chartOptions={CHART_OPTIONS.quotaHoldersChartOptions}
                    period={calculatedPeriod}
                    renderLastValue={(lastValue: number) =>
                      formatNumber(lastValue, { thousandMask: true })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UiCardChart
                    hasWatermark
                    cardTitle="Drawdown"
                    loading={loadingSeries}
                    cardSubTitle={fundHeader.nicename}
                    tooltip={TOOLTIPS_TEXTS.drawdownFundComparison}
                    series={drawDownSeries}
                    chartOptions={CHART_OPTIONS.dragDownChartOptions}
                    period={calculatedPeriod}
                    renderLastValue={(lastValue: number) =>
                      formatNumber(lastValue, { type: 'percent' })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UiCardChart
                    hasWatermark
                    cardTitle="Patrimônio"
                    loading={loadingSeries}
                    cardSubTitle={fundHeader.nicename}
                    tooltip={TOOLTIPS_TEXTS.netWorth}
                    series={netWorthSeries}
                    chartOptions={CHART_OPTIONS.netWorthChartOptions}
                    period={calculatedPeriod}
                    renderLastValue={(lastValue: number) =>
                      formatNumber(lastValue, {
                        type: 'money',
                        thousandMask: true,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UiCardChart
                    hasWatermark
                    cardTitle="Volatilidade"
                    emptyMessage="Se este fundo tiver menos de 21 dias, pode ser que ainda não possua dados suficientes para compor o gráfico, mas se o período for maior que isso, por favor, entre em contato conosco para solucionarmos."
                    cardSubTitle={fundHeader.nicename}
                    loading={loadingSeries}
                    tooltip={TOOLTIPS_TEXTS.volatilityFundComparison}
                    series={volatilitySeries}
                    chartOptions={CHART_OPTIONS.volatilityChartOptions}
                    period={calculatedPeriod}
                    renderLastValue={(lastValue: number) =>
                      formatNumber(lastValue, { type: 'percent' })
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        <UiFundRelated fundRelated={relatedFunds} />
      </Box>
    </DefaultLayoutTools>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return { fallback: 'blocking', paths: [] };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params.slug as string;

  const sheetFundHeader = makeSheetFundHeader();
  const { fundHeader, notFound, redirect } = await sheetFundHeader.execute(
    slug
  );

  if (redirect) {
    return { redirect: { destination: redirect, statusCode: 301 } };
  }

  if (notFound) {
    return { notFound: true };
  }

  return {
    props: { fundHeader },
    revalidate: REVALIDATE_TIME_PAGES.sheetFundPage,
  };
};

export default FundPage;
